(() => {
  const ROOT_SELECTOR = '[data-tan-quick-quote][data-homepage-mode="true"]';
  const SCROLL_QUIET_MS = 600;
  const scheduledRoots = new WeakMap();
  const localizedCopyFor = (root) => {
    let dictionary = {};
    try {
      const parsed = JSON.parse(root.querySelector('[data-tan-quick-quote-copy]')?.textContent || '{}');
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) dictionary = parsed;
    } catch (_) {
      dictionary = {};
    }
    return (key) => (typeof dictionary[key] === 'string' ? dictionary[key] : '');
  };

  const loadScript = (src, assetName) => {
    if (!src) return Promise.reject(new Error(`Missing ${assetName} source`));

    const existing = document.querySelector(`script[data-tan-quick-asset="${assetName}"]`);
    if (existing?.dataset.loaded === 'true') return Promise.resolve();

    return new Promise((resolve, reject) => {
      const script = existing || document.createElement('script');
      const cleanup = () => {
        window.clearTimeout(timeoutId);
        script.removeEventListener('load', onLoad);
        script.removeEventListener('error', onError);
      };
      const onLoad = () => {
        cleanup();
        script.dataset.loaded = 'true';
        resolve();
      };
      const onError = () => {
        cleanup();
        script.remove();
        reject(new Error(`Unable to load ${assetName}`));
      };
      const timeoutId = window.setTimeout(() => {
        cleanup();
        script.remove();
        reject(new Error(`${assetName} timed out`));
      }, 15000);

      script.addEventListener('load', onLoad);
      script.addEventListener('error', onError);

      if (!existing) {
        script.src = src;
        script.async = false;
        script.dataset.tanQuickAsset = assetName;
        document.head.appendChild(script);
      }
    });
  };

  const safeCurrencyFallback = () => {
    window.TAN_QUOTE_CURRENCY_CONFIG = {
      baseCurrency: 'EUR',
      asOf: 'Unavailable',
      source: 'Currency conversion temporarily unavailable',
      currencies: { EUR: { rate: 1, roundTo: 10 } },
    };
  };

  const showLoadError = (root) => {
    const notice = root.querySelector('[data-quick-error]');
    if (!notice) return;
    const copy = localizedCopyFor(root);

    notice.hidden = false;
    notice.replaceChildren(document.createTextNode(`${copy('load_error')} `));
    const link = document.createElement('a');
    link.href = root.dataset.quickFallbackUrl || '/pages/custom-neon-sign-quote#quote-start';
    link.textContent = copy('open_full_upload_page');
    notice.appendChild(link);
  };

  const loadAssets = (root) => {
    if (window.TANQuickQuoteRuntime?.scan) {
      window.TANQuickQuoteRuntime.scan(root);
      return Promise.resolve();
    }

    if (!window.__tanHomeQuickQuoteAssetsPromise) {
      const assetsPromise = (async () => {
        try {
          await loadScript(root.dataset.quickCurrencySrc, 'currency-rates');
        } catch (error) {
          safeCurrencyFallback();
        }

        await loadScript(root.dataset.quickRuntimeSrc, 'quote-runtime');
        if (!window.TANQuickQuoteRuntime?.scan) throw new Error('Quote runtime unavailable');
      })();
      window.__tanHomeQuickQuoteAssetsPromise = assetsPromise;
      assetsPromise.catch(() => {
        if (window.__tanHomeQuickQuoteAssetsPromise === assetsPromise) {
          window.__tanHomeQuickQuoteAssetsPromise = null;
        }
      });
    }

    root.dataset.quickQuoteAssetsLoading = 'true';
    return window.__tanHomeQuickQuoteAssetsPromise
      .then(() => {
        delete root.dataset.quickQuoteAssetsError;
        const notice = root.querySelector('[data-quick-error]');
        if (notice) {
          notice.hidden = true;
          notice.textContent = '';
        }
        window.TANQuickQuoteRuntime.scan(root);
        root.dataset.quickQuoteAssetsReady = 'true';
      })
      .catch((error) => {
        root.dataset.quickQuoteAssetsError = 'true';
        showLoadError(root);
        throw error;
      })
      .finally(() => {
        delete root.dataset.quickQuoteAssetsLoading;
      });
  };

  const scheduleRoot = (root) => {
    if (!root || scheduledRoots.has(root)) return;

    const disposers = [];
    let observer;
    let started = false;
    let quietQueued = false;
    let quietTimer = 0;
    let visibilityHandler = null;
    const scrollTargets = [window, document.querySelector('.page-wrapper')].filter(
      (target, index, values) => target && values.indexOf(target) === index
    );

    // Upload-Lift can mount before the deferred quote runtime. Keep the
    // lightweight placeholder in sync so customers never see it covering an
    // uploader that is already ready to use.
    root.querySelectorAll('[data-upload-app-slot]').forEach((slot) => {
      const placeholder = slot.querySelector('[data-upload-lift-placeholder]');
      if (!placeholder) return;

      const syncPlaceholder = () => {
        placeholder.hidden = Array.from(slot.children).some((child) => child !== placeholder);
      };
      const uploadObserver = new MutationObserver(syncPlaceholder);
      const onUploadReady = () => { placeholder.hidden = true; };

      uploadObserver.observe(slot, { childList: true });
      slot.addEventListener('upload:ready', onUploadReady);
      syncPlaceholder();

      disposers.push(() => {
        uploadObserver.disconnect();
        slot.removeEventListener('upload:ready', onUploadReady);
      });
    });

    const clearQuietWait = () => {
      window.clearTimeout(quietTimer);
      quietTimer = 0;
      scrollTargets.forEach((target) => target.removeEventListener('scroll', postponeQuietStart));
      if (visibilityHandler) {
        document.removeEventListener('visibilitychange', visibilityHandler);
        visibilityHandler = null;
      }
      quietQueued = false;
    };

    const finishQuietStart = () => {
      if (document.visibilityState !== 'visible') {
        if (!visibilityHandler) {
          visibilityHandler = () => {
            if (document.visibilityState !== 'visible') return;
            document.removeEventListener('visibilitychange', visibilityHandler);
            visibilityHandler = null;
            postponeQuietStart();
          };
          document.addEventListener('visibilitychange', visibilityHandler);
        }
        return;
      }

      clearQuietWait();
      void start().catch(() => {});
    };

    function postponeQuietStart() {
      window.clearTimeout(quietTimer);
      quietTimer = window.setTimeout(finishQuietStart, SCROLL_QUIET_MS);
    }

    const queueQuietStart = () => {
      if (started || quietQueued) return;
      quietQueued = true;
      observer?.disconnect();
      scrollTargets.forEach((target) => target.addEventListener('scroll', postponeQuietStart, { passive: true }));
      postponeQuietStart();
    };

    const start = () => {
      if (started) return loadAssets(root);
      started = true;
      clearQuietWait();
      observer?.disconnect();
      disposers.forEach((dispose) => dispose());
      return loadAssets(root);
    };

    const addTrigger = (eventName, options) => {
      const handler = () => void start().catch(() => {});
      root.addEventListener(eventName, handler, options);
      disposers.push(() => root.removeEventListener(eventName, handler, options));
    };

    // Pointer/touch start also fires when the user is only swiping the page.
    // Keep actual interactions immediate without executing the form runtime
    // in the middle of a vertical scroll gesture.
    addTrigger('click');
    addTrigger('focusin');

    const captureAction = (event) => {
      if (root.dataset.quickQuoteReady === 'true') return;
      const control = event.target.closest?.('[data-quick-next],[data-quick-back],[data-quick-submit]');
      if (!control || !root.contains(control)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      control.disabled = true;
      start()
        .then(() => {
          control.disabled = false;
          if (control.isConnected) control.click();
        })
        .catch(() => {
          control.disabled = false;
        });
    };
    root.addEventListener('click', captureAction, true);

    // A successful Shopify contact submission reloads the page. On the homepage
    // the quote sits well below the fold, so the lazy observer may never start
    // the runtime that restores the customer to the confirmation panel.
    if (root.querySelector('[data-quick-success]')) {
      void start().catch(() => {});
    } else if ('IntersectionObserver' in window && !window.Shopify?.designMode) {
      observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) queueQuietStart();
      }, { rootMargin: '1200px 0px', threshold: 0 });
      observer.observe(root);
    } else {
      queueQuietStart();
    }

    scheduledRoots.set(root, () => {
      clearQuietWait();
      observer?.disconnect();
      disposers.forEach((dispose) => dispose());
      root.removeEventListener('click', captureAction, true);
    });
  };

  const scan = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) scheduleRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(scheduleRoot);
  };

  const boot = () => scan();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const roots = event.target.matches?.(ROOT_SELECTOR)
      ? [event.target]
      : Array.from(event.target.querySelectorAll?.(ROOT_SELECTOR) || []);
    roots.forEach((root) => {
      scheduledRoots.get(root)?.();
      scheduledRoots.delete(root);
    });
  });
})();
